import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tag as TagIcon, Plus, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Per-customer tag editor.
 *
 * - Free-form chip input (type + Enter / comma to add)
 * - Suggestions sourced from /api/customers/tags (existing tags on this
 *   business). Click to add. Already-added tags are hidden from the
 *   suggestion list.
 * - Backend deduplicates case-insensitively and caps at 10 per customer.
 * - Saves immediately on every change via PUT /customers/{id} so the user
 *   never has to remember to press a separate "save" button — matches the
 *   feel of common CRMs (HubSpot, Notion).
 *
 * Props:
 *   customerId   - string (required)
 *   initialTags  - string[]
 *   onChange     - optional callback receiving the new tag list
 */
export function TagEditor({ customerId, initialTags = [], onChange }) {
  const [tags, setTags] = useState(initialTags);
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]); // [{name,count}]
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Keep local state in sync if parent reloads the customer record.
  useEffect(() => { setTags(initialTags || []); }, [JSON.stringify(initialTags)]); // eslint-disable-line

  useEffect(() => {
    let alive = true;
    api.get("/customers/tags")
      .then((r) => alive && setSuggestions(r.data || []))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const lowerCurrent = useMemo(
    () => new Set(tags.map((t) => t.toLowerCase())),
    [tags]
  );

  // Show up to 8 suggestions that the customer doesn't already have.
  // If the user is typing, also filter by the prefix.
  const visibleSuggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    return suggestions
      .filter((s) => !lowerCurrent.has(s.name.toLowerCase()))
      .filter((s) => (q ? s.name.toLowerCase().includes(q) : true))
      .slice(0, 8);
  }, [suggestions, lowerCurrent, input]);

  const persist = async (next) => {
    setSaving(true);
    try {
      await api.put(`/customers/${customerId}`, { tags: next });
      onChange?.(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke lagre tagger");
      // Revert on failure so UI stays truthful.
      setTags(tags);
      return false;
    } finally {
      setSaving(false);
    }
    return true;
  };

  const addTag = async (raw) => {
    const t = String(raw || "").trim();
    if (!t) return;
    if (lowerCurrent.has(t.toLowerCase())) {
      setInput("");
      return;
    }
    if (tags.length >= 10) {
      toast.error("Maks 10 tagger per kunde");
      return;
    }
    const next = [...tags, t.slice(0, 40)];
    setTags(next);
    setInput("");
    const ok = await persist(next);
    // Optimistically also bump the suggestion list so the new tag appears
    // in future autocompletes during this session.
    if (ok && !suggestions.some((s) => s.name.toLowerCase() === t.toLowerCase())) {
      setSuggestions((prev) => [...prev, { name: t, count: 1 }]);
    }
  };

  const removeTag = async (t) => {
    const next = tags.filter((x) => x !== t);
    setTags(next);
    await persist(next);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      // Quick-remove last tag when the input is empty
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className="space-y-2" data-testid="tag-editor">
      <Label className="flex items-center gap-1.5">
        <TagIcon className="w-4 h-4 text-primary" strokeWidth={1.75} />
        Tagger
        {saving && <span className="text-[10px] text-muted-foreground">Lagrer…</span>}
      </Label>

      <div
        className="flex flex-wrap items-center gap-1.5 rounded-xl border border-input bg-background px-2 py-1.5 min-h-[40px] focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-accent text-primary border border-primary/20"
            data-testid={`tag-chip-${t}`}
          >
            {t}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(t); }}
              className="hover:text-destructive transition-colors"
              aria-label={`Fjern tag ${t}`}
              data-testid={`tag-remove-${t}`}
            >
              <X className="w-3 h-3" strokeWidth={2.25} />
            </button>
          </span>
        ))}
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => input.trim() && addTag(input)}
          placeholder={tags.length === 0 ? "Skriv en tag og trykk Enter…" : ""}
          className="flex-1 min-w-[120px] border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-1 h-7 text-sm bg-transparent"
          data-testid="tag-editor-input"
        />
      </div>

      {visibleSuggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="tag-suggestions">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground self-center mr-0.5">
            Forslag
          </span>
          {visibleSuggestions.map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() => addTag(s.name)}
              className="text-xs px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:bg-accent/60 hover:text-foreground inline-flex items-center gap-1 transition-colors"
              data-testid={`tag-suggest-${s.name}`}
            >
              <Plus className="w-3 h-3" strokeWidth={2} />
              {s.name}
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground leading-snug">
        Bruk tagger for å gruppere kunder — f.eks. <em>VIP</em>, <em>Fargekunde</em> eller <em>Klippekort</em>. Du kan filtrere kundelisten på tagger.
      </p>
    </div>
  );
}
